import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useEscapeKey } from '../hooks/useEscapeKey';
import { submissions, coins as coinsApi } from '../utils/api';
import CoinSelector from './shared/CoinSelector';

export default function SubmissionModal({ initialData, onClose }) {
  const navigate = useNavigate();
  useEscapeKey(onClose);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [allCoins, setAllCoins] = useState([]);
  const [coinsLoading, setCoinsLoading] = useState(true);
  
  const [formData, setFormData] = useState(() => {
    if (initialData) {
      return {
        ...initialData,
        coins: Array.isArray(initialData.coins) ? initialData.coins.map(coin => ({
          ...coin,
          line_number: coin.line_number || ''
        })) : []
      };
    }

    return {
      grading_service_id: '1', // PCGS ID
      submission_number: '',
      order_number: '',
      description: '',
      coins: []
    };
  });

  const isEditMode = Boolean(initialData);

  // Fetch all coins for the selector
  useEffect(() => {
    const fetchCoins = async () => {
      setCoinsLoading(true);
      try {
        const response = await coinsApi.getAll();
        setAllCoins(response);
      } catch (err) {
        console.error('Failed to fetch coins:', err);
        setError('Failed to load coins');
      } finally {
        setCoinsLoading(false);
      }
    };
    fetchCoins();
  }, []);

  const validateForm = () => {
    // Check if at least one coin is selected
    if (formData.coins.length === 0) {
      setError('Please select at least one coin for the submission');
      return false;
    }

    // Validate line numbers
    const lineNumbers = formData.coins.map(c => parseInt(c.line_number));
    const hasEmptyLineNumbers = lineNumbers.some(num => !num);
    const hasDuplicates = new Set(lineNumbers).size !== lineNumbers.length;
    
    if (hasEmptyLineNumbers) {
      setError('All coins must have a line number');
      return false;
    }
    
    if (hasDuplicates) {
      setError('Line numbers must be unique');
      return false;
    }

    return true;
  };

  const handleCoinSelection = (selectedIds) => {
    // Get the full coin objects for selected IDs
    const selectedCoins = allCoins
      .filter(coin => selectedIds.includes(coin.id))
      .map((coin, index) => ({
        ...coin,
        line_number: (index + 1).toString()
      }));
    
    setFormData(prev => ({
      ...prev,
      coins: selectedCoins
    }));
  };

  const handleRemoveCoin = (coinId) => {
    const updatedCoins = formData.coins
      .filter(coin => coin.id !== coinId)
      .map((coin, index) => ({
        ...coin,
        line_number: (index + 1).toString()
      }));
    
    setFormData(prev => ({
      ...prev,
      coins: updatedCoins
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      if (!validateForm()) {
        setLoading(false);
        return;
      }

      if (isEditMode) {
        await submissions.update(initialData.id, formData);
        onClose();
      } else {
        await submissions.create(formData);
        onClose();
        navigate('/submissions');
      }
    } catch (err) {
      setError('Failed to create submission');
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-6xl w-full max-h-[90vh] flex flex-col">
        {/* Fixed Header */}
        <div className="flex justify-between items-center px-6 py-4 border-b">
          <h2 className="text-xl font-semibold text-gray-900">
            {isEditMode ? 'Edit Submission' : 'Create New Submission'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500"
          >
            <span className="sr-only">Close</span>
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Error Message */}
        {error && (
          <div className="px-6 py-4 bg-red-50">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-red-700">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex flex-col min-h-0 flex-1">
          {/* Scrollable Content */}
          <div className="flex-1 overflow-y-auto p-6">
            <div className="flex gap-6">
              {/* Left Column - Form Fields */}
              <div className="flex-1 space-y-6">
                <div className="flex gap-4">
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-gray-700">
                      Grading Service
                    </label>
                    <div className="mt-1">
                      <input
                        type="text"
                        value="PCGS"
                        disabled
                        className="block w-full border-gray-300 rounded-md shadow-sm bg-gray-50 text-gray-500 sm:text-sm"
                      />
                      <p className="mt-1 text-sm text-gray-500">Only PCGS submissions are supported at this time</p>
                    </div>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-gray-700">
                      Submission Number
                    </label>
                    <input
                      type="text"
                      value={formData.submission_number}
                      onChange={(e) => setFormData({
                        ...formData,
                        submission_number: e.target.value
                      })}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                      required
                    />
                  </div>

                  <div className="flex-1">
                    <label className="block text-sm font-medium text-gray-700">
                      Order Number
                    </label>
                    <input
                      type="text"
                      value={formData.order_number}
                      onChange={(e) => setFormData({
                        ...formData,
                        order_number: e.target.value
                      })}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Description
                  </label>
                  <textarea
                    value={formData.description || ''}
                    onChange={(e) => setFormData({
                      ...formData,
                      description: e.target.value
                    })}
                    maxLength={1000}
                    rows={3}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    placeholder="Enter submission description (optional)"
                  />
                  <p className="mt-1 text-sm text-gray-500">
                    {((formData.description || '').length)}/1000 characters
                  </p>
                </div>

                {/* Selected Coins List with Line Numbers */}
                {formData.coins.length > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Coins in Submission ({formData.coins.length})
                    </label>
                    <div className="bg-gray-50 rounded-md p-4">
                      <div className="space-y-2">
                        {formData.coins.map((coin, index) => (
                          <div key={coin.id} className="flex items-center justify-between space-x-4 bg-white p-2 rounded border">
                            <div className="flex-1 min-w-0">
                              <span className="text-sm text-gray-900 font-medium">
                                {coin.user_coin_id}
                              </span>
                              <span className="text-sm text-gray-500 ml-2">
                                {[
                                  coin.country,
                                  coin.year && coin.mint_mark ? `${coin.year}-${coin.mint_mark}` : coin.year || '',
                                  coin.assayer,
                                  coin.denomination,
                                  coin.variety
                                ].filter(Boolean).join(' ')}
                              </span>
                            </div>
                            <div className="flex items-center space-x-3">
                              <div className="flex items-center space-x-2">
                                <label className="text-sm text-gray-500">Line #:</label>
                                <input
                                  type="number"
                                  min="1"
                                  value={coin.line_number}
                                  onChange={(e) => {
                                    const newCoins = [...formData.coins];
                                    newCoins[index] = {
                                      ...newCoins[index],
                                      line_number: e.target.value
                                    };
                                    setFormData({
                                      ...formData,
                                      coins: newCoins
                                    });
                                  }}
                                  className="w-20 text-sm border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                                  required
                                />
                              </div>
                              <button
                                type="button"
                                onClick={() => handleRemoveCoin(coin.id)}
                                className="text-red-500 hover:text-red-700"
                                title="Remove coin"
                              >
                                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Right Column - Coin Selector */}
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Coins for Submission
                </label>
                {coinsLoading ? (
                  <div className="flex items-center justify-center py-4">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600"></div>
                    <span className="ml-2 text-sm text-gray-500">Loading coins...</span>
                  </div>
                ) : (
                  <CoinSelector
                    selectedCoinIds={formData.coins.map(c => c.id)}
                    onChange={handleCoinSelection}
                    id="submission-coin-selector"
                    showDetails={true}
                  />
                )}
              </div>
            </div>
          </div>
          
          {/* Fixed Footer */}
          <div className="px-6 py-4 border-t bg-white">
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading || formData.coins.length === 0}
                className="px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
              >
                {loading ? (isEditMode ? 'Saving...' : 'Creating...') : (isEditMode ? 'Save Changes' : 'Create Submission')}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
